import { debounce } from "@/helpers/Misc";
import * as _ from "lodash";

export default class LookupService {
  public static list: Map<string, Array<ILookup> | { [key: string]: ILookup }> = new Map();
  public static get(id: string) {
    return this.list.get(id);
  }

  public static async loadLookups(_lookups: { [key: string]: { [key: string]: ILookup } | [ILookup] }) {
    let speed = false;
    let debug = false;

    const startTime: any = speed && performance.now();

    for (let item in _lookups) {
      debug && console.log("Lookup: " + item);
      debounce(()=>{console.log("debounce finished")},10000);

      await this.loadLookup(item, _lookups[item]).then((r) => {});
      debug && console.log("--------");
    }

    const endTime: any = speed && performance.now();
    const executionTime: any = speed && endTime - startTime;
    if (speed) {
      console.log("-------");
      console.log(`loadLookups: ${executionTime} ms`);
      console.log("-------");
    }
  }

  public static async loadLookup(lookupName: string, _lookupOptions: Array<ILookup> | { [key: string]: ILookup }) {
    let speed = false;
    let debug = false;
    const startTime: any = speed && performance.now();

    let setAndProcessLookup: boolean = false;
    const lookupVersion = this.loadLookupFromStorage(lookupName, "lookupVersion");
    if (lookupVersion) {
      const lookupCompare = await this.getDataHash(_lookupOptions);

      if (_.isEqual(lookupCompare, lookupVersion)) {
        debug && console.log("Is equal");
        const lookupFinal = this.loadLookupFromStorage(lookupName);
        this.setLookup(lookupName, lookupFinal);
      } else {
        debug && console.log("Is different");
        await this.saveLookupVersion(lookupName, _lookupOptions);
        setAndProcessLookup = true;
      }
    } else {
      debug && console.log("Lookups version not exist.");
      await this.saveLookupVersion(lookupName, _lookupOptions);
      setAndProcessLookup = true;
    }

    if (setAndProcessLookup) {
      const lookupFinal: ILookup[] = this.processLookup(_lookupOptions);
      debug && console.log(lookupFinal);
      this.saveLookupFinal(lookupName, lookupFinal);
      this.setLookup(lookupName, lookupFinal);
    }

    const endTime: any = speed && performance.now();
    const executionTime: any = speed && endTime - startTime;
    if (speed) {
      console.log("-------");
      console.log(`loadLookup: ${executionTime} ms`);
      console.log("-------");
    }
    return lookupName;
  }

  public static loadExternalLookups() {
    let speed = false;
    let debug = false;

    const startTime: any = speed && performance.now();

    const externalLookups = window.FormEngine.engineConfig.lookups;
    if (externalLookups) {
      let lookups = externalLookups.items;
      if (lookups && lookups.length) {
        for (let item in lookups) {
          const lookup = lookups[item];
          this.loadExternalLookup(lookup);
        }
      }
    }
    const endTime: any = speed && performance.now();
    const executionTime: any = speed && endTime - startTime;
    if (speed) {
      console.log("-------");
      console.log(`loadExternalLookups: ${executionTime} ms`);
      console.log("-------");
    }
  }

  public static loadExternalLookup(lookup: any) {
    let speed = false;
    let debug = false;

    const externalLookups = window.FormEngine.engineConfig.lookups;
    let lookupGlobalUrl = this.hasAttr(externalLookups.globalUrl) ? externalLookups.globalUrl : null;
    let lookupGlobalHandler = this.hasFn(externalLookups, "globalHandler") ? externalLookups.globalHandler : null;
    let lookupGlobalHook = this.hasFn(externalLookups, "globalHook") ? externalLookups.globalHook : null;
    let handler = this.hasFn(lookup, "handler") ? lookup.handler : lookupGlobalHandler || null;
    let hook = this.hasFn(lookup, "hook") ? lookup.hook : lookupGlobalHook || null;
    let id = this.hasAttr(lookup.id) ? lookup.id : null;
    let url = this.hasAttr(lookup.url) ? lookup.url : lookupGlobalUrl || null;
    const lookupUrl: string = url + id;

    if (handler) {
      handler(this);
      return;
    }

    if (lookupUrl && typeof lookupUrl === "string" && lookupUrl.trim() !== "") {
      fetch(lookupUrl)
        .then(async (response) => {
          if (!response.ok) {
            throw new Error("Load data error");
          }
          return response.text();
        })
        .then((data) => {
          try {
            const startTime: any = speed && performance.now();

            const processedData = this.processExternalLookup(data);
            const lookupName = "EXT" + id;
            const lookupFinal = processedData.aaData;

            if (hook) {
              hook(this, lookupFinal);
            }

            this.loadLookup(lookupName, lookupFinal).then((r) => {});

            debug && console.log(lookupName);
            debug && console.log(lookupFinal);

            const endTime: any = speed && performance.now();
            const executionTime: any = speed && endTime - startTime;
            if (speed) {
              console.log("-------");
              console.log(`loadExternalLookup: ${executionTime} ms`);
              console.log("-------");
            }
          } catch (error) {
            console.error("JSON Parse error:", error);
          }
        })
        .catch((error) => {
          console.error("Fetch lookups error: ", error);
        });
    }
  }

  public static processExternalLookup(data: any) {
    const cleanedData = JSON.parse(data.trim().slice(2, -3));

    let transformedData = cleanedData.aaData.map((item: any[]) => {
      let dataItem: ILookup = { Value: item[0], Text: item[1] };
      dataItem = this.addLookupParameters(dataItem); // part of processLookup()
      return dataItem;
    });

    let finalData = {
      ...cleanedData,
      aaData: transformedData,
    };

    return finalData;
  }

  public static setLookup(lookupName: string, _lookupOptions: Array<ILookup> | { [key: string]: ILookup }) {
    this.list.set(lookupName, _lookupOptions);
  }

  public static loadLookupFromStorage(lookupName: string, prefix: string = "lookupFinal") {
    return window.FormEngine.storage.get(prefix + "." + lookupName);
  }

  public static async saveLookupVersion(lookupName: string, _lookupOptions: Array<ILookup> | { [key: string]: ILookup }) {
    const lookupsVersion = await this.getDataHash(_lookupOptions);
    window.FormEngine.storage.save("lookupVersion." + lookupName, lookupsVersion);
  }

  public static saveLookupFinal(lookupName: string, _lookupOptions: Array<ILookup> | { [key: string]: ILookup }) {
    window.FormEngine.storage.save("lookupFinal." + lookupName, _lookupOptions);
  }

  public static processLookup(_lookupOptions: Array<ILookup> | { [key: string]: ILookup }) {
    if (typeof _lookupOptions === "object" && _lookupOptions !== null && !Array.isArray(_lookupOptions)) {
      let repairedLookups = [];
      let lookups = _.cloneDeep(_lookupOptions as { [key: string]: ILookup });

      for (let lookupKey in lookups) {
        let lookup = lookups[lookupKey as string] as ILookup;
        lookup = this.addLookupParameters(lookup);

        repairedLookups.push(lookup);
      }

      _lookupOptions = repairedLookups as Array<ILookup>;
    }

    return _lookupOptions;
  }

  public static addLookupParameters(_lookupOptions: ILookup) {
    if (!_lookupOptions.Disabled) {
      _lookupOptions.Disabled = false;
    }

    return _lookupOptions;
  }

  public static hasAttr(attribute: string): boolean {
    return !!(attribute && typeof attribute === "string" && attribute.trim() !== "");
  }

  public static hasFn(obj: any, propertyName: string): boolean {
    return !!(obj[propertyName] && typeof obj[propertyName] === "function");
  }

  public static async getDataHash(data: any) {
    let speed = false;
    const startTime: any = speed && performance.now();

    if (!Array.isArray(data)) {
      data = [...Object.values(data)];
    }

    let dataHash = await generateSHA256Hash(data)
      .then((hash) => {
        return hash;
      })
      .catch((error) => console.error("Error while generating hash:", error));

    const endTime: any = speed && performance.now();
    const executionTime: any = speed && endTime - startTime;
    if (speed) {
      console.log("-------");
      console.log(`getDataHash: ${executionTime} ms`);
      console.log("-------");
    }

    return dataHash;

    async function generateSHA256Hash(data: string) {
      let speed = false;
      const startTime: any = speed && performance.now();

      data = JSON.stringify(data);

      const hash = crypto.subtle.digest("SHA-256", new TextEncoder().encode(data));
      const hashOutput = hash.then((buffer) => {
        const hashArray = Array.from(new Uint8Array(buffer));
        return hashArray.map((byte) => byte.toString(16).padStart(2, "0")).join("");
      });

      const endTime: any = speed && performance.now();
      const executionTime: any = speed && endTime - startTime;
      if (speed) {
        console.log("-------");
        console.log(`generateSHA256Hash: ${executionTime} ms`);
        console.log("-------");
      }

      return new Promise((resolve) => {
        resolve(hashOutput);
      });
    }
  }
}
